import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { useEffect, useState } from "react";

import { ApiError, api, type EditableField, type EstoqueState, type Fonte, type Session } from "../lib/api";
import { Badge, Modal, btnGhost, btnPrimary, inp, toast } from "./ui";

// Tool global como sessão de bloco. O cliente vê "uma configuração"; por trás é
// uma tool. Campos editáveis (tool_overrides) + widget "Conectar estoque" quando
// a tool usa {{ sistema.estoque }} — o slug nunca aparece pro cliente.

const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "var(--ink-secondary)" };
const DISCONNECT_WARN = "Isso APAGARÁ A CONEXÃO DO ESTOQUE, seu agente não conseguirá mais acessar os veículos.";

export function EditableInput({ f, value, onChange }: { f: EditableField; value: any; onChange: (v: any) => void }) {
  if (f.type === "bool") {
    return <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} style={{ accentColor: "var(--brand)" }} />{f.label}</label>;
  }
  if (f.type === "number") return <input type="number" style={inp} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />;
  if (f.type === "textarea") return <textarea rows={4} style={{ ...inp, resize: "vertical", lineHeight: 1.45 }} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  return <input style={inp} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
}

function ago(iso: string | null): string {
  if (!iso) return "";
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 1 ? "agora" : m < 60 ? `há ${m} min` : m < 1440 ? `há ${Math.round(m / 60)} h` : `há ${Math.round(m / 1440)} d`;
}

export function EstoqueWidget({ agentId, initial }: { agentId: string; initial: EstoqueState }) {
  const qc = useQueryClient();
  const { data: state = initial } = useQuery<EstoqueState>({
    queryKey: ["estoque", agentId], queryFn: () => api.get(`/agents/${agentId}/estoque`), initialData: initial,
    refetchInterval: (q) => (q.state.data?.state === "connecting" ? 5000 : false),
  });
  const { data: fontes = [] } = useQuery<Fonte[]>({ queryKey: ["estoque-fontes"], queryFn: () => api.get("/estoque-fontes") });
  const { data: existing = [] } = useQuery<{ client_id: string; name: string; vehicle_count: number; agent: string }[]>({ queryKey: ["estoques", agentId], queryFn: () => api.get(`/estoques?exclude=${agentId}`) });
  const [fonteKey, setFonteKey] = useState(initial.fonte ?? "");
  const [urls, setUrls] = useState<string[]>(initial.urls.length ? initial.urls : [""]);
  const [tutorial, setTutorial] = useState(false);
  const [editing, setEditing] = useState(false);       // "Trocar URLs"
  const [disc, setDisc] = useState<0 | 1 | 2>(0);
  const [catalog, setCatalog] = useState(false);       // "Ver estoque": catálogo da carga parseada
  useEffect(() => { if (state.fonte && !fonteKey) setFonteKey(state.fonte); }, [state.fonte, fonteKey]);
  const fonte = fontes.find((f) => f.key === fonteKey);
  const done = (msg: string) => () => { qc.invalidateQueries({ queryKey: ["estoque", agentId] }); qc.invalidateQueries({ queryKey: ["estoques"] }); setEditing(false); toast(msg); };
  const fail = (e: unknown) => toast((e as ApiError).detail ?? "Erro");
  const connect = useMutation({ mutationFn: (body: object) => api.post(`/agents/${agentId}/estoque/connect`, body), onSuccess: done("Lendo seu estoque…"), onError: fail });
  const change = useMutation({ mutationFn: () => api.post(`/agents/${agentId}/estoque/urls`, { urls }), onSuccess: done("Lendo seu estoque…"), onError: fail });
  const retry = useMutation({ mutationFn: () => api.post(`/agents/${agentId}/estoque/retry`, {}), onSuccess: done("Tentando de novo…"), onError: fail });
  const disconnect = useMutation({ mutationFn: () => api.post(`/agents/${agentId}/estoque/disconnect`, {}), onSuccess: () => { setDisc(0); done("Estoque desconectado")(); }, onError: fail });
  const busy = connect.isPending || change.isPending || retry.isPending || disconnect.isPending;

  const urlForm = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {!editing && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={lbl}>Sistema de estoque</span>
          <select style={inp} value={fonteKey} onChange={(e) => setFonteKey(e.target.value)}>
            <option value="">Escolha…</option>
            {fontes.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={lbl}>{urls.length > 1 ? "URLs do estoque" : "URL do estoque"}</span>
        {urls.map((u, i) => (
          <div key={i} style={{ display: "flex", gap: 6 }}>
            <input style={inp} value={u} placeholder="https://…" onChange={(e) => setUrls((p) => p.map((x, j) => (j === i ? e.target.value : x)))} />
            {urls.length > 1 && <button style={btnGhost} onClick={() => setUrls((p) => p.filter((_, j) => j !== i))}>×</button>}
          </div>
        ))}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={btnGhost} onClick={() => setUrls((p) => [...p, ""])}>+ Outra URL</button>
          {fonte?.tutorial_html && <button style={btnGhost} onClick={() => setTutorial(true)}>Encontre o link</button>}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {state.state === "connected" && !editing && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Badge tone="ok">Conectado · {state.vehicle_count} veículo{state.vehicle_count === 1 ? "" : "s"}</Badge>
            <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>atualizado {ago(state.last_updated_at)}</span>
            {state.shared_by.length > 0 && <span style={{ fontSize: 12, color: "var(--ink-dim)" }}>· também usado em {state.shared_by.join(", ")}</span>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={btnPrimary} onClick={() => setCatalog(true)}>Ver estoque</button>
            <button style={btnGhost} onClick={() => setEditing(true)}>Trocar URLs</button>
            <button style={{ ...btnGhost, color: "var(--danger)" }} onClick={() => setDisc(1)}>Desconectar</button>
          </div>
          {catalog && <Catalog agentId={agentId} onClose={() => setCatalog(false)} />}
        </>
      )}
      {state.state === "connecting" && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Badge tone="neutral">Lendo seu estoque…</Badge>
          <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>pode levar alguns minutos</span>
        </div>
      )}
      {state.state === "error" && !editing && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <Badge tone="warn">Não conseguimos ler o estoque</Badge>
            <span style={{ fontSize: 13, color: "var(--ink)" }}>{state.error?.friendly}</span>
            {state.error?.code && (
              <span style={{ fontSize: 12, color: "var(--ink-muted)", display: "flex", alignItems: "center", gap: 8 }}>
                Código: <code style={{ fontFamily: "var(--font-mono)", background: "var(--surface-hover)", padding: "1px 6px", borderRadius: 4 }}>{state.error.code}</code>
                <button style={{ ...btnGhost, padding: "2px 8px", fontSize: 11 }} onClick={() => { navigator.clipboard?.writeText(state.error!.code!).catch(() => {}); toast("Código copiado"); }}>copiar</button>
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={btnPrimary} disabled={busy} onClick={() => retry.mutate()}>Tentar de novo</button>
            <button style={btnGhost} onClick={() => setEditing(true)}>Trocar URLs</button>
            <button style={{ ...btnGhost, color: "var(--danger)" }} onClick={() => setDisc(1)}>Desconectar</button>
          </div>
        </>
      )}
      {(state.state === "disconnected" || editing) && (
        <>
          {urlForm}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {editing ? (
              <>
                <button style={btnPrimary} disabled={busy || !urls.some((u) => u.trim())} onClick={() => change.mutate()}>Salvar e reprocessar</button>
                <button style={btnGhost} onClick={() => { setEditing(false); setUrls(state.urls.length ? state.urls : [""]); }}>Cancelar</button>
              </>
            ) : (
              <>
                <button style={btnPrimary} disabled={busy || !fonteKey || !urls.some((u) => u.trim())} onClick={() => connect.mutate({ fonte_key: fonteKey, urls })}>{state.had ? "Conectar novamente" : "Conectar"}</button>
                {existing.length > 0 && (
                  <select style={{ ...inp, width: "auto" }} value="" onChange={(e) => { if (e.target.value) connect.mutate({ client_id: e.target.value }); }}>
                    <option value="">usar estoque já conectado…</option>
                    {existing.map((x) => <option key={x.client_id} value={x.client_id}>{x.agent} · {x.vehicle_count} veículos</option>)}
                  </select>
                )}
              </>
            )}
          </div>
        </>
      )}

      {tutorial && fonte && (
        <Modal title={`Encontre o link · ${fonte.label}`} onClose={() => setTutorial(false)} width={640}>
          <div className="ss-tutorial" style={{ fontSize: 14, lineHeight: 1.6, maxHeight: "60vh", overflowY: "auto" }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fonte.tutorial_html, { ADD_TAGS: ["video", "source"], ADD_ATTR: ["controls", "src"] }) }} />
        </Modal>
      )}
      {disc === 1 && (
        <Modal title="Desconectar o estoque?" onClose={() => setDisc(0)}>
          <p style={{ fontSize: 13, color: "var(--ink-muted)", lineHeight: 1.5 }}>Você poderá conectar de novo com as mesmas URLs depois.</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}><button style={btnGhost} onClick={() => setDisc(0)}>Cancelar</button><button style={btnPrimary} onClick={() => setDisc(2)}>Continuar</button></div>
        </Modal>
      )}
      {disc === 2 && (
        <Modal title="Tem certeza?" onClose={() => setDisc(0)}>
          <p style={{ fontSize: 13, color: "var(--danger)", fontWeight: 600, lineHeight: 1.5 }}>{DISCONNECT_WARN}</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}><button style={btnGhost} onClick={() => setDisc(0)}>Cancelar</button><button style={btnPrimary} disabled={busy} onClick={() => disconnect.mutate()}>Desconectar agora</button></div>
        </Modal>
      )}
    </div>
  );
}

// Catálogo simples da carga parseada: 3 por linha, 1ª foto, marca + modelo. Só pra o cliente
// conferir o que o agente enxerga; o JSON inteiro fica no Valquiria.
type Veiculo = { id: string; marca: string; modelo: string; versao: string; ano: string; preco: number | null; foto: string | null };
const brl = (v: number | null) => (typeof v === "number" ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }) : null);

function Catalog({ agentId, onClose }: { agentId: string; onClose: () => void }) {
  const { data, isLoading, error } = useQuery<Veiculo[]>({ queryKey: ["estoque-veiculos", agentId], queryFn: () => api.get(`/agents/${agentId}/estoque/veiculos`) });
  const list = data ?? [];
  return (
    <Modal title={`Estoque${list.length ? ` · ${list.length} veículo${list.length === 1 ? "" : "s"}` : ""}`} onClose={onClose} width={960}>
      <div style={{ maxHeight: "70vh", overflowY: "auto", margin: "0 -4px", padding: "0 4px" }}>
        {isLoading && <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>Carregando…</div>}
        {error && <div style={{ fontSize: 13, color: "var(--danger)" }}>Não foi possível carregar o estoque.</div>}
        {!isLoading && !error && list.length === 0 && <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>Nenhum veículo na última carga.</div>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 14 }}>
          {list.map((v) => (
            <div key={v.id || `${v.marca}-${v.modelo}-${v.ano}`} style={{ border: "1px solid var(--line)", borderRadius: 10, overflow: "hidden", background: "var(--surface-card)" }}>
              <div style={{ aspectRatio: "4 / 3", background: "var(--surface-hover)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {v.foto ? <img src={v.foto} alt={`${v.marca} ${v.modelo}`} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  : <span style={{ fontSize: 12, color: "var(--ink-dim)" }}>sem foto</span>}
              </div>
              <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 2 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.marca} {v.modelo}</div>
                <div style={{ fontSize: 12, color: "var(--ink-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{[v.versao, v.ano].filter(Boolean).join(" · ")}</div>
                {brl(v.preco) && <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-secondary)", marginTop: 4 }}>{brl(v.preco)}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

export function ToolCard({ agentId, s, overrides, onOverride }: {
  agentId: string; s: Session; overrides: Record<string, any>; onOverride: (path: string, v: any) => void;
}) {
  return (
    <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 18 }}>
      {s.estoque && <EstoqueWidget agentId={agentId} initial={s.estoque} />}
      {(s.editable ?? []).map((f) => (
        <div key={f.path} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {f.type !== "bool" && <label style={lbl}>{f.label}</label>}
          <EditableInput f={f} value={overrides[f.path] ?? f.value} onChange={(v) => onOverride(f.path, v)} />
          {f.help && <div style={{ fontSize: 12, color: "var(--ink-dim)" }}>{f.help}</div>}
        </div>
      ))}
    </div>
  );
}
